/**
 * AdminVault — LastPass-style vault manager for admin
 * 
 * Features:
 * - Card-based list with icons per category
 * - Shows: friendly name, category badge, level (system/customer), owner email
 * - Machine-linked items show agent name + hostname
 * - Search + category filter + level filter
 * - View details with decrypted value (click to reveal)
 * - Edit, Delete with confirmation
 * - Responsive dark theme
 */

import { useState, useEffect, useCallback } from 'react';
import { SkeletonBar } from '../ui';
import { T } from '../../styles/tokens';
import { Button, Textarea } from '../ui';

// ─ Types ─
interface EnrichedData {
  machine_agent_name: string | null;
  machine_hostname: string | null;
  machine_customer_email: string | null;
  owner_email: string | null;
  owner_name: string | null;
  is_system: boolean;
  credential_type: string | null;
  runner_name: string | null;
  last4: string | null;
  brand: string | null;
  ip: string | null;
  username: string | null;
}

interface VaultItem {
  id: string;
  customer_id: string;
  name: string;
  description: string | null;
  category: string;
  value_hash: string;
  metadata: string | null;
  created_by: string;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  _enriched: EnrichedData;
}

type FilterCategory = string;
type FilterLevel = 'all' | 'platform' | 'account' | 'machine' | 'agent';

const LIMIT = 200;

// ─ Category config ─
const CATEGORY_CONFIG: Record<string, { icon: string; label: string; color: string }> = {
  // Servidor
  'machine_ssh': { icon: '', label: 'SSH Máquina', color: T.success },
  'server-ssh': { icon: '', label: 'SSH Servidor', color: T.primary },
  // Agente
  'machine_openclaw_token': { icon: '', label: 'OpenClaw Token', color: T.purple },
  'machine_runner_token': { icon: '', label: 'Runner Token', color: T.warning },
  // Integrações
  'api-keys': { icon: '', label: 'API Key', color: T.danger },
  'integration-github': { icon: '', label: 'GitHub', color: T.text },
  'integration-stripe': { icon: '', label: 'Stripe', color: '#635BFF' },
  'integration-cloudflare': { icon: '', label: 'Cloudflare', color: '#F6821F' },
  'integration-hostinger': { icon: '', label: 'Hostinger', color: '#673DE6' },
  'integration-trello': { icon: '', label: 'Trello', color: '#0079BF' },
  'integration-sendgrid': { icon: '', label: 'SendGrid', color: '#1A82E2' },
  'integration': { icon: '', label: 'Integração', color: T.danger },
  // Pagamento
  'saved_card': { icon: '', label: 'Cartão Salvo', color: T.danger },
  // Genéricos
  'password': { icon: '', label: 'Senha', color: T.textMuted },
  'token': { icon: '', label: 'Token', color: '#14B8A6' },
  'secure-note': { icon: '', label: 'Nota Segura', color: T.purple },
  'database': { icon: '', label: 'Banco de Dados', color: T.warning },
  'domain-dns': { icon: '', label: 'Domínio/DNS', color: '#06B6D4' },
  'email-smtp': { icon: '', label: 'Email/SMTP', color: '#F43F5E' },
  'certificate': { icon: '', label: 'Certificado', color: '#84CC16' },
  'other': { icon: '', label: 'Outro', color: T.textMuted },
};

function getCategoryConfig(cat: string) {
  return CATEGORY_CONFIG[cat] || { icon: '', label: cat, color: T.textMuted };
}

// ─ Friendly name parser ─
function getFriendlyName(item: VaultItem): { title: string; subtitle: string } {
  const e = item._enriched;

  // Machine SSH / OpenClaw Token / Runner Token
  if (item.name.startsWith('machine:')) {
    const agentName = e.machine_agent_name;
    const hostname = e.machine_hostname || e.runner_name;
    if (agentName && hostname) return { title: agentName, subtitle: hostname };
    if (agentName) return { title: agentName, subtitle: 'Máquina Cloud' };
    if (hostname) return { title: hostname, subtitle: item.category === 'machine_runner_token' ? 'Runner' : 'Máquina' };
    // fallback: extract machine_id
    const parts = item.name.split(':');
    return { title: parts[1]?.slice(0, 12) + '...' || item.name, subtitle: getCategoryConfig(item.category).label };
  }

  // SSH Credentials
  if (item.name.startsWith('SSH Credentials')) {
    const agentName = e.machine_agent_name;
    const hostname = e.machine_hostname;
    if (agentName) return { title: `SSH — ${agentName}`, subtitle: hostname || 'Servidor' };
    if (hostname) return { title: `SSH — ${hostname}`, subtitle: 'Servidor' };
    return { title: item.name, subtitle: item.description || '' };
  }

  // Bridge Server
  if (item.name.includes('Bridge')) {
    return { title: item.name, subtitle: e.ip || item.description || '' };
  }

  // Saved Card
  if (item.category === 'saved_card') {
    const brand = (e.brand || '').toUpperCase();
    return { title: `${brand} ****${e.last4 || '????'}`, subtitle: item.description || 'Cartão salvo' };
  }

  // API Keys
  if (item.category === 'api-keys') {
    return { title: item.name, subtitle: item.description || 'API Key' };
  }

  return { title: item.name, subtitle: item.description || '' };
}

// ─ Owner info ─
function getOwnerInfo(item: VaultItem): { label: string; isSystem: boolean } {
  const e = item._enriched;
  // For machine credentials saved by SYSTEM, show the machine's actual customer
  if (e.is_system && e.machine_customer_email) {
    return { label: e.machine_customer_email, isSystem: false };
  }
  if (e.is_system) return { label: 'Sistema (Elon)', isSystem: true };
  if (e.owner_email) return { label: e.owner_email, isSystem: false };
  if (e.machine_customer_email) return { label: e.machine_customer_email, isSystem: false };
  return { label: item.customer_id.slice(0, 12) + '...', isSystem: false };
}

// ─ Level badge ─
// 4 levels: Plataforma (infra Elon Cloud), Conta (cliente), Máquina (servidor), Agente (IA)
// Priority: check is_system FIRST — if SYSTEM owns it, it's platform-managed
function getLevelBadge(item: VaultItem): { label: string; color: string; level: string } {
  const e = item._enriched;
  const isSystem = e.is_system;

  // SYSTEM-owned credentials = managed by Elon Cloud platform
  if (isSystem) {
    // Sub-classify system credentials
    if (item.category === 'machine_openclaw_token' || item.category === 'machine_runner_token') {
      return { label: 'Plataforma · Agente', color: T.warning, level: 'platform' };
    }
    if (item.category === 'machine_ssh') {
      return { label: 'Plataforma · Máquina', color: T.warning, level: 'platform' };
    }
    if (item.category === 'api-keys' || item.category.startsWith('integration')) {
      return { label: 'Plataforma · API', color: T.warning, level: 'platform' };
    }
    return { label: 'Plataforma', color: T.warning, level: 'platform' };
  }

  // Customer-owned credentials
  // SSH keys saved per-customer (server-ssh category)
  if (item.category === 'server-ssh') {
    return { label: 'Máquina', color: T.primary, level: 'machine' };
  }

  // Customer agent tokens (rare — usually SYSTEM manages these)
  if (item.category === 'machine_openclaw_token' || item.category === 'machine_runner_token') {
    return { label: 'Agente', color: T.purple, level: 'agent' };
  }

  // Customer personal data (cards, passwords)
  if (item.category === 'saved_card' || item.category === 'password') {
    return { label: 'Conta', color: T.success, level: 'account' };
  }

  // Fallback
  return { label: 'Conta', color: T.success, level: 'account' };
}

export function AdminVault() {
  const [items, setItems] = useState<VaultItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<FilterCategory>('all');
  const [filterLevel, setFilterLevel] = useState<FilterLevel>('all');

  // Detail modal
  const [selectedItem, setSelectedItem] = useState<VaultItem | null>(null);
  const [decryptedValue, setDecryptedValue] = useState<string | null>(null);
  const [decryptLoading, setDecryptLoading] = useState(false);
  const [showValue, setShowValue] = useState(false);

  // Edit modal
  const [editItem, setEditItem] = useState<VaultItem | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', category: '', value: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Delete modal
  const [deleteItem, setDeleteItem] = useState<VaultItem | null>(null);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', description: '', category: 'server-ssh', value: '' });

  const fetchItems = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ page: String(p), limit: String(LIMIT) });
      if (search) params.append('search', search);
      if (filterCategory !== 'all') params.append('category', filterCategory);

      const res = await fetch(`/api/v1/admin/vault/items?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const data = json.data || json;

      let filtered = data.items || [];
      // Client-side level filter using getLevelBadge
      if (filterLevel !== 'all') {
        filtered = filtered.filter((i: VaultItem) => getLevelBadge(i).level === filterLevel);
      }

      setItems(filtered);
      setTotal(data.total || 0);
      setPage(p);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro desconhecido');
    } finally {
      setLoading(false);
    }
  }, [search, filterCategory, filterLevel]);

  useEffect(() => { fetchItems(1); }, [fetchItems]);

  const handleSearch = () => setSearch(searchInput);

  // Decrypt value
  const handleDecrypt = async (itemId: string) => {
    setDecryptLoading(true);
    try {
      const res = await fetch(`/api/v1/admin/vault/items/${itemId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      const data = json.data || json;
      setDecryptedValue(data.decrypted_value || '(vazio)');
      setShowValue(true);
    } catch {
      setDecryptedValue('Erro ao descriptografar');
    } finally {
      setDecryptLoading(false);
    }
  };

  // Edit
  const handleEditSubmit = async () => {
    if (!editItem) return;
    setEditLoading(true);
    try {
      const payload: any = { name: editForm.name, description: editForm.description, category: editForm.category };
      if (editForm.value.trim()) payload.value = editForm.value;
      const res = await fetch(`/api/v1/admin/vault/items/${editItem.id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setEditItem(null);
      fetchItems(page);
    } catch (err) {
      alert(`Erro: ${err instanceof Error ? err.message : 'Desconhecido'}`);
    } finally {
      setEditLoading(false);
    }
  };

  // Delete
  const handleDelete = async () => {
    if (!deleteItem) return;
    try {
      const res = await fetch(`/api/v1/admin/vault/items/${deleteItem.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteItem(null);
      fetchItems(page);
    } catch (err) {
      alert(`Erro: ${err instanceof Error ? err.message : 'Desconhecido'}`);
    }
  };

  // Create
  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.value.trim()) { alert('Nome e valor são obrigatórios'); return; }
    try {
      const res = await fetch('/api/v1/vault/items', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(createForm),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setShowCreate(false);
      setCreateForm({ name: '', description: '', category: 'server-ssh', value: '' });
      fetchItems(1);
    } catch (err) {
      alert(`Erro: ${err instanceof Error ? err.message : 'Desconhecido'}`);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const formatDate = (d: string) => {
    const date = new Date(d);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'agora';
    if (diffMin < 60) return `${diffMin}min atrás`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h atrás`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d atrás`;
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  // ─ Stats (from total API count + current page items for level breakdown) ─
  const stats = {
    total,
    platform: items.filter(i => getLevelBadge(i).level === 'platform').length,
    account: items.filter(i => getLevelBadge(i).level === 'account').length,
    machine: items.filter(i => getLevelBadge(i).level === 'machine').length,
    agent: items.filter(i => getLevelBadge(i).level === 'agent').length,
  };

  return (
    <div style={S.container}>
      {/* Header */}
      <div style={S.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 32 }}></span>
          <div>
            <h1 style={S.title}>Elon Password Vault</h1>
            <p style={S.subtitle}>Gerencie credenciais, tokens e senhas com segurança AES-256</p>
          </div>
        </div>
        <Button variant="ghost" onClick={() => setShowCreate(true)} style={S.createBtn}>
          + Novo Secret
        </Button>
      </div>

      {/* Stats bar */}
      <div style={S.statsBar}>
        {[
          { label: 'Total', value: stats.total, icon: '' },
          { label: 'Plataforma', value: stats.platform, icon: '' },
          { label: 'Conta', value: stats.account, icon: '' },
          { label: 'Máquina', value: stats.machine, icon: '' },
          { label: 'Agente', value: stats.agent, icon: '' },
        ].map(s => (
          <div key={s.label} style={S.statCard}>
            <span style={{ fontSize: 20 }}>{s.icon}</span>
            <div>
              <div style={{ fontSize: 18, fontWeight: 700, color: T.text }}>{s.value}</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={S.filtersRow}>
        <div style={S.searchBox}>
          <input
            type="text"
            placeholder=" Buscar por nome, agente, hostname, email..."
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            onKeyDown={(e: any) => e.key === 'Enter' && handleSearch()}
            style={S.searchInput}
          />
          {searchInput && (
            <Button variant="ghost" onClick={() => { setSearchInput(''); setSearch(''); }} style={S.clearBtn}></Button>
          )}
        </div>
        <select value={filterCategory} onChange={(e: any) => setFilterCategory(e.target.value)} style={S.filterSelect}>
          <option value="all">Todas categorias</option>
          {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
            <option key={k} value={k}>{v.icon} {v.label}</option>
          ))}
        </select>
        <select value={filterLevel} onChange={(e: any) => setFilterLevel(e.target.value)} style={S.filterSelect}>
          <option value="all">Todos níveis</option>
          <option value="platform">Plataforma</option>
          <option value="account">Conta</option>
          <option value="machine">Máquina</option>
          <option value="agent">Agente</option>
        </select>
      </div>

      {/* Error */}
      {error && <div style={S.errorBox}>{error}</div>}

      {/* Loading */}
      {loading && (
        <div style={S.loadingBox}>
          <div style={S.spinner} />
          <div style={{ padding: 12 }}><SkeletonBar width="60%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={14} /></div>
        </div>
      )}

      {/* Items list */}
      {!loading && items.length > 0 && (
        <div style={S.itemsList}>
          {items.map(item => {
            const { title, subtitle } = getFriendlyName(item);
            const catConf = getCategoryConfig(item.category);
            const owner = getOwnerInfo(item);
            const level = getLevelBadge(item);

            return (
              <div
                key={item.id}
                style={S.itemCard}
                onClick={() => { setSelectedItem(item); setDecryptedValue(null); setShowValue(false); }}
                onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = catConf.color; e.currentTarget.style.background = T.bgCard; }}
                onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.bgHover; e.currentTarget.style.background = T.bgSubtle; }}
              >
                {/* Icon */}
                <div style={{ ...S.itemIcon, background: `${catConf.color}20`, color: catConf.color }}>
                  {catConf.icon}
                </div>

                {/* Main info */}
                <div style={S.itemMain}>
                  <div style={S.itemTitle}>{title}</div>
                  <div style={S.itemSubtitle}>{subtitle}</div>
                </div>

                {/* Category badge */}
                <div style={{ ...S.badge, background: `${catConf.color}20`, color: catConf.color }}>
                  {catConf.label}
                </div>

                {/* Level badge */}
                <div style={{ ...S.badge, background: `${level.color}20`, color: level.color, minWidth: 80 }}>
                  {level.label}
                </div>

                {/* Owner */}
                <div style={S.itemOwner}>
                  <div style={{ fontSize: 12, color: owner.isSystem ? T.purple : T.text }}>
                    {owner.label}
                  </div>
                </div>

                {/* Updated */}
                <div style={S.itemDate}>
                  {formatDate(item.updated_at)}
                </div>

                {/* Actions */}
                <div style={S.itemActions} onClick={(e: any) => e.stopPropagation()}>
                  <Button variant="ghost"
                    onClick={() => { setEditItem(item); setEditForm({ name: item.name, description: item.description || '', category: item.category, value: '' }); }}
                    style={S.actionBtn} title="Editar"
                  ></Button>
                  <Button variant="ghost"
                    onClick={() => setDeleteItem(item)}
                    style={{ ...S.actionBtn, color: T.danger }} title="Deletar"
                  ></Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Empty */}
      {!loading && items.length === 0 && !error && (
        <div style={S.emptyBox}>
          <span style={{ fontSize: 48 }}></span>
          <p style={{ fontSize: 16, color: T.textMuted, marginTop: 12 }}>Nenhuma credencial encontrada</p>
        </div>
      )}

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div style={S.pagination}>
          <Button variant="ghost" onClick={() => fetchItems(page - 1)} disabled={page === 1} style={S.pageBtn}>← Anterior</Button>
          <span style={{ fontSize: 12, color: T.textMuted }}>
            Página {page} de {totalPages} ({total} itens)
          </span>
          <Button variant="ghost" onClick={() => fetchItems(page + 1)} disabled={page === totalPages} style={S.pageBtn}>Próxima →</Button>
        </div>
      )}

      {/* ═══ DETAIL MODAL ═══ */}
      {selectedItem && (
        <>
          <div style={S.overlay} onClick={() => setSelectedItem(null)} />
          <div style={S.modal}>
            <div style={S.modalHeader}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 24 }}>{getCategoryConfig(selectedItem.category).icon}</span>
                <div>
                  <h2 style={{ margin: 0, fontSize: 18 }}>{getFriendlyName(selectedItem).title}</h2>
                  <p style={{ margin: 0, fontSize: 12, color: T.textMuted }}>{getFriendlyName(selectedItem).subtitle}</p>
                </div>
              </div>
              <Button variant="ghost" onClick={() => setSelectedItem(null)} style={S.closeBtn}></Button>
            </div>
            <div style={S.modalBody}>
              {/* Info grid */}
              <div style={S.detailGrid}>
                <DetailRow label="ID" value={selectedItem.id} mono />
                <DetailRow label="Nome técnico" value={selectedItem.name} mono />
                <DetailRow label="Categoria" value={getCategoryConfig(selectedItem.category).label} />
                <DetailRow label="Nível" value={getLevelBadge(selectedItem).label} />
                <DetailRow label="Owner" value={getOwnerInfo(selectedItem).label} />
                {selectedItem._enriched.machine_agent_name && (
                  <DetailRow label="Agente" value={selectedItem._enriched.machine_agent_name} />
                )}
                {selectedItem._enriched.machine_hostname && (
                  <DetailRow label="Hostname" value={selectedItem._enriched.machine_hostname} />
                )}
                {selectedItem._enriched.username && (
                  <DetailRow label="Usuário" value={selectedItem._enriched.username} />
                )}
                {selectedItem._enriched.ip && (
                  <DetailRow label="IP" value={selectedItem._enriched.ip} />
                )}
                {selectedItem._enriched.last4 && (
                  <DetailRow label="Cartão" value={`****${selectedItem._enriched.last4} (${selectedItem._enriched.brand?.toUpperCase()})`} />
                )}
                <DetailRow label="Criado em" value={new Date(selectedItem.created_at).toLocaleString('pt-BR')} />
                <DetailRow label="Atualizado" value={new Date(selectedItem.updated_at).toLocaleString('pt-BR')} />
                {selectedItem.description && (
                  <DetailRow label="Descrição" value={selectedItem.description} />
                )}
              </div>

              {/* Reveal value */}
              <div style={{ marginTop: 20, padding: 16, borderRadius: 10, background: T.blackAlpha30, border: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: T.textMuted, textTransform: 'uppercase' }}>Valor criptografado</label>
                  {!showValue ? (
                    <Button variant="ghost"
                      onClick={() => handleDecrypt(selectedItem.id)}
                      disabled={decryptLoading}
                      style={{ ...S.revealBtn }}
                    >
                      {decryptLoading ? 'Descriptografando...' : 'Revelar valor'}
                    </Button>
                  ) : (
                    <Button variant="ghost" onClick={() => setShowValue(false)} style={S.revealBtn}>
                      Ocultar
                    </Button>
                  )}
                </div>
                {showValue && decryptedValue && (
                  <div style={{ position: 'relative' }}>
                    <pre style={S.valueBox}>{decryptedValue}</pre>
                    <Button variant="ghost"
                      onClick={() => { navigator.clipboard.writeText(decryptedValue); }}
                      style={S.copyBtn}
                      title="Copiar"
                    >Copiar</Button>
                  </div>
                )}
                {!showValue && (
                  <div style={{ fontFamily: 'monospace', fontSize: 14, color: T.borderHover, letterSpacing: 3 }}>
                    ••••••••••••••••••••
                  </div>
                )}
              </div>
            </div>
            <div style={S.modalFooter}>
              <Button variant="ghost" onClick={() => setSelectedItem(null)} style={S.modalBtn}>Fechar</Button>
            </div>
          </div>
        </>
      )}

      {/* ═══ EDIT MODAL ═══ */}
      {editItem && (
        <>
          <div style={S.overlay} onClick={() => setEditItem(null)} />
          <div style={S.modal}>
            <div style={S.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Editar Credencial</h2>
              <Button variant="ghost" onClick={() => setEditItem(null)} style={S.closeBtn}></Button>
            </div>
            <div style={S.modalBody}>
              <FormField label="Nome" value={editForm.name} onChange={v => setEditForm({ ...editForm, name: v })} />
              <FormField label="Descrição" value={editForm.description} onChange={v => setEditForm({ ...editForm, description: v })} textarea />
              <div style={S.formGroup}>
                <label style={S.formLabel}>Categoria</label>
                <select value={editForm.category} onChange={(e: any) => setEditForm({ ...editForm, category: e.target.value })} style={S.formInput}>
                  {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>{v.icon} {v.label}</option>
                  ))}
                </select>
              </div>
              <FormField label="Novo valor (deixe vazio para manter)" value={editForm.value} onChange={v => setEditForm({ ...editForm, value: v })} password />
            </div>
            <div style={S.modalFooter}>
              <Button variant="ghost" onClick={() => setEditItem(null)} style={S.modalBtn}>Cancelar</Button>
              <Button variant="ghost" onClick={handleEditSubmit} disabled={editLoading} style={S.modalBtnPrimary}>
                {editLoading ? 'Salvando...' : 'Salvar'}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* ═══ DELETE MODAL ═══ */}
      {deleteItem && (
        <>
          <div style={S.overlay} onClick={() => setDeleteItem(null)} />
          <div style={{ ...S.modal, maxWidth: 440 }}>
            <div style={S.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Confirmar Exclusão</h2>
              <Button variant="ghost" onClick={() => setDeleteItem(null)} style={S.closeBtn}></Button>
            </div>
            <div style={S.modalBody}>
              <p style={{ fontSize: 14, color: T.text }}>
                Tem certeza que deseja deletar <strong>{getFriendlyName(deleteItem).title}</strong>?
              </p>
              <p style={{ fontSize: 12, color: T.textMuted, marginTop: 8 }}>
                Esta ação não pode ser desfeita. A credencial será removida permanentemente.
              </p>
            </div>
            <div style={S.modalFooter}>
              <Button variant="ghost" onClick={() => setDeleteItem(null)} style={S.modalBtn}>Cancelar</Button>
              <Button variant="ghost" onClick={handleDelete} style={S.modalBtnDanger}>Deletar Permanentemente</Button>
            </div>
          </div>
        </>
      )}

      {/* ═══ CREATE MODAL ═══ */}
      {showCreate && (
        <>
          <div style={S.overlay} onClick={() => setShowCreate(false)} />
          <div style={S.modal}>
            <div style={S.modalHeader}>
              <h2 style={{ margin: 0, fontSize: 18 }}>Novo Secret</h2>
              <Button variant="ghost" onClick={() => setShowCreate(false)} style={S.closeBtn}></Button>
            </div>
            <div style={S.modalBody}>
              <FormField label="Nome *" value={createForm.name} onChange={v => setCreateForm({ ...createForm, name: v })} placeholder="ex: Servidor SSH prod-01" />
              <FormField label="Descrição" value={createForm.description} onChange={v => setCreateForm({ ...createForm, description: v })} textarea placeholder="ex: Root access para servidor de produção" />
              <div style={S.formGroup}>
                <label style={S.formLabel}>Categoria</label>
                <select value={createForm.category} onChange={(e: any) => setCreateForm({ ...createForm, category: e.target.value })} style={S.formInput}>
                  {Object.entries(CATEGORY_CONFIG).map(([k, v]) => (
                    <option key={k} value={k}>{v.icon} {v.label}</option>
                  ))}
                </select>
              </div>
              <FormField label="Valor/Senha *" value={createForm.value} onChange={v => setCreateForm({ ...createForm, value: v })} textarea mono placeholder="ex: root:s3cureP@ss!" />
            </div>
            <div style={S.modalFooter}>
              <Button variant="ghost" onClick={() => setShowCreate(false)} style={S.modalBtn}>Cancelar</Button>
              <Button variant="ghost" onClick={handleCreate} style={S.modalBtnPrimary}>Criar Secret</Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─ Helper components ─
function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '8px 0', borderBottom: `1px solid ${T.whiteAlpha5}` }}>
      <span style={{ fontSize: 12, color: T.textMuted, fontWeight: 600, minWidth: 100 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', textAlign: 'right', wordBreak: 'break-all', fontFamily: mono ? 'monospace' : 'inherit', maxWidth: '70%' }}>{value}</span>
    </div>
  );
}

function FormField({ label, value, onChange, textarea, password, mono, placeholder }: {
  label: string; value: string; onChange: (v: string) => void;
  textarea?: boolean; password?: boolean; mono?: boolean; placeholder?: string;
}) {
  return (
    <div style={S.formGroup}>
      <label style={S.formLabel}>{label}</label>
      {textarea ? (
        <Textarea
          value={value} onChange={(e: any) => onChange(e.target.value)}
          style={{ ...S.formInput, minHeight: 80, resize: 'vertical' as any, fontFamily: mono ? 'monospace' : 'inherit' }}
          placeholder={placeholder}
        />
      ) : (
        <input
          type={password ? 'password' : 'text'}
          value={value} onChange={(e: any) => onChange(e.target.value)}
          style={{ ...S.formInput, fontFamily: mono ? 'monospace' : 'inherit' }}
          placeholder={placeholder}
        />
      )}
    </div>
  );
}

// ─ Styles ─
const S: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px 20px', maxWidth: 1100, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24,
  },
  title: { margin: 0, fontSize: 24, fontWeight: 700, color: T.text },
  subtitle: { margin: '4px 0 0', fontSize: 13, color: T.textMuted },
  createBtn: {
    background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)', color: T.text, border: 'none',
    padding: '10px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer',
  },
  statsBar: {
    display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' as any,
  },
  statCard: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
    borderRadius: 10, background: T.bgCard, border: '1px solid rgba(255,255,255,0.06)',
    flex: '1 1 100px', minWidth: 100,
  },
  filtersRow: {
    display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' as any,
  },
  searchBox: {
    position: 'relative' as any, flex: 1, minWidth: 250,
  },
  searchInput: {
    width: '100%', padding: '10px 36px 10px 14px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)', background: T.bgCard,
    color: T.text, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' as any,
  },
  clearBtn: {
    position: 'absolute' as any, right: 10, top: '50%', transform: 'translateY(-50%)',
    background: 'none', border: 'none', color: T.textMuted, cursor: 'pointer', fontSize: 14,
  },
  filterSelect: {
    padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
    background: T.bgCard, color: T.text, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit',
  },
  errorBox: {
    padding: '12px 16px', borderRadius: 8, background: T.dangerBg,
    border: `1px solid ${T.dangerBgStrong}`, color: T.danger, fontSize: 13, marginBottom: 16,
  },
  loadingBox: {
    padding: '60px 20px', textAlign: 'center' as any,
  },
  spinner: {
    width: 32, height: 32, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: T.primary,
    borderRadius: '50%', margin: '0 auto 12px',
    animation: 'spin 1s linear infinite',
  },
  itemsList: {
    display: 'flex', flexDirection: 'column' as any, gap: 6,
  },
  itemCard: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
    borderRadius: 10, background: T.bgSubtle, border: `1px solid ${T.whiteAlpha8}`,
    cursor: 'pointer', transition: 'all 0.15s',
  },
  itemIcon: {
    width: 40, height: 40, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18, flexShrink: 0,
  },
  itemMain: {
    flex: 1, minWidth: 0,
  },
  itemTitle: {
    fontSize: 14, fontWeight: 600, color: T.text, whiteSpace: 'nowrap' as any, overflow: 'hidden', textOverflow: 'ellipsis',
  },
  itemSubtitle: {
    fontSize: 12, color: T.textMuted, whiteSpace: 'nowrap' as any, overflow: 'hidden', textOverflow: 'ellipsis', marginTop: 2,
  },
  badge: {
    display: 'inline-flex', alignItems: 'center', padding: '4px 8px', borderRadius: 6,
    fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap' as any, flexShrink: 0,
  },
  itemOwner: {
    width: 140, flexShrink: 0, textAlign: 'right' as any, overflow: 'hidden',
  },
  itemDate: {
    width: 70, flexShrink: 0, fontSize: 11, color: 'rgba(255,255,255,0.35)', textAlign: 'right' as any,
  },
  itemActions: {
    display: 'flex', gap: 4, flexShrink: 0,
  },
  actionBtn: {
    background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '4px 6px',
    borderRadius: 6, transition: 'background 0.15s',
  },
  emptyBox: {
    padding: '60px 20px', textAlign: 'center' as any, borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.06)', background: T.bgSubtle,
  },
  pagination: {
    display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, marginTop: 20, padding: 16,
  },
  pageBtn: {
    padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
    background: 'transparent', color: T.text, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },

  // Modals
  overlay: {
    position: 'fixed' as any, top: 0, left: 0, right: 0, bottom: 0,
    background: T.bgOverlay, zIndex: 999, backdropFilter: 'blur(4px)',
  },
  modal: {
    position: 'fixed' as any, top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
    width: '92%', maxWidth: 600, maxHeight: '90vh', overflowY: 'auto' as any,
    background: T.bg, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 14, zIndex: 1000,
  },
  modalHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px 20px', borderBottom: `1px solid ${T.whiteAlpha8}`,
  },
  closeBtn: {
    width: 32, height: 32, borderRadius: 8, border: 'none', background: T.bgCardHover,
    color: T.textMuted, cursor: 'pointer', fontSize: 16,
  },
  modalBody: { padding: 20 },
  modalFooter: {
    display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px',
    borderTop: `1px solid ${T.whiteAlpha8}`,
  },
  modalBtn: {
    padding: '8px 16px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)',
    background: 'transparent', color: T.text, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  modalBtnPrimary: {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)', color: T.text,
    cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  modalBtnDanger: {
    padding: '8px 16px', borderRadius: 8, border: 'none',
    background: T.danger, color: T.text, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  detailGrid: { display: 'flex', flexDirection: 'column' as any },
  revealBtn: {
    padding: '6px 12px', borderRadius: 6, border: `1px solid ${T.whiteAlpha15}`,
    background: T.bgCardHover, color: T.text, cursor: 'pointer',
    fontSize: 12, fontWeight: 600,
  },
  valueBox: {
    padding: 12, borderRadius: 8, background: T.bgOverlay, border: '1px solid rgba(255,255,255,0.1)',
    color: T.success, fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-wrap' as any,
    wordBreak: 'break-all' as any, margin: 0, maxHeight: 200, overflowY: 'auto' as any,
  },
  copyBtn: {
    position: 'absolute' as any, top: 8, right: 8, padding: '4px 8px', borderRadius: 4,
    border: 'none', background: T.bgActive, color: T.text,
    cursor: 'pointer', fontSize: 11,
  },
  formGroup: { marginBottom: 14 },
  formLabel: {
    display: 'block', fontSize: 12, fontWeight: 600, color: T.textMuted,
    marginBottom: 6, textTransform: 'uppercase' as any,
  },
  formInput: {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: '1px solid rgba(255,255,255,0.1)', background: T.bgCard,
    color: T.text, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' as any,
  },
};
